import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Button, Checkbox } from './';
import { FormattedMessage } from '../_library/intl';
import { api, useAppSelector } from '../_library';
import { userActions } from '../_actions';

// Per-language links to the Walletto terms documents.
// TODO: point en/ru at the English pages once they exist - for now every
// language opens the Latvian documents, as agreed.
const TERMS_LINKS: { [lang: string]: { cardTerms: string, generalTerms: string } } = {
    lv: {
        cardTerms: 'https://bonusukarte.lv/lv/walletto-karsu-noteikumi',
        generalTerms: 'https://bonusukarte.lv/lv/walletto-visparejie-darijuma-noteikumi',
    },
    en: {
        cardTerms: 'https://bonusukarte.lv/lv/walletto-karsu-noteikumi',
        generalTerms: 'https://bonusukarte.lv/lv/walletto-visparejie-darijuma-noteikumi',
    },
    ru: {
        cardTerms: 'https://bonusukarte.lv/lv/walletto-karsu-noteikumi',
        generalTerms: 'https://bonusukarte.lv/lv/walletto-visparejie-darijuma-noteikumi',
    },
};

/**
 * Blocking popup shown right after login to users whose current company is a
 * Walletto one and who have not yet accepted the current terms redaction
 * (profile flag wallettoTermsAcceptanceRequired).
 *
 * Deliberately NOT built on the shared Modal component: that one always
 * renders a close icon and closes on ESC/overlay click, while this dialog must
 * be impossible to dismiss without accepting. Acceptance is stored once per
 * user; after a successful accept the profile is reloaded and the flag turns
 * false, which unmounts the popup for good.
 */
// Routes wrapped in PrivateRoute, i.e. the authenticated part of the app. The
// modal must only ever appear there: a stored token can still be valid while
// the visitor is looking at the login or another public screen (e.g. a fresh
// tab), and the popup has no business covering those.
const PRIVATE_PATH_PREFIXES = ['/dashboard', '/transactions', '/users', '/messaging', '/reporting', '/settings', '/files'];

export const WallettoTermsModal: () => JSX.Element | null = () => {
    const dispatch = useDispatch();
    const location = useLocation();
    const user = useAppSelector(state => state.user);
    const lang = useAppSelector(state => state.i18n.lang);

    // pre-checked by request: the user can still untick either box, which
    // disables the confirm button, but the default flow is a single click
    const [cardTermsRead, setCardTermsRead] = useState(true);
    const [generalTermsRead, setGeneralTermsRead] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [hasError, setHasError] = useState(false);

    const isPrivateRoute =
        PRIVATE_PATH_PREFIXES.some(prefix =>
            location.pathname === prefix || location.pathname.startsWith(prefix + '/')) &&
        // /users/email/confirm/:token is public although it shares the /users prefix
        !location.pathname.startsWith('/users/email/confirm');

    if (!isPrivateRoute || !user.loggedIn || !user.wallettoTermsAcceptanceRequired) {
        return null;
    }

    const links = TERMS_LINKS[lang] || TERMS_LINKS.lv;
    const version = user.wallettoTermsVersion;

    const onConfirm = () => {
        setIsSubmitting(true);
        setHasError(false);

        api.post('/users/terms/accept', {})
            .then(() => {
                // reload the profile: the flag comes back false and the modal
                // unmounts; isSubmitting stays true meanwhile so the button
                // cannot be pressed twice
                (userActions.loadSettings())(dispatch);
            })
            .catch(() => {
                setHasError(true);
                setIsSubmitting(false);
            });
    };

    const renderTermsCheckbox = (
        checked: boolean,
        onChange: (checked: boolean) => void,
        linkHref: string,
        linkLabel: string,
    ) => (
        <div className="border border-gray-200 rounded-lg p-4 mb-3 flex items-start space-x-3 bg-white">
            <Checkbox checked={checked} onChange={onChange} />
            <div>
                <span className="text-gray-900">
                    <FormattedMessage id="bk.walletto_terms.checkbox_prefix" />{' '}
                    <a
                        href={linkHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline text-bk-p font-medium"
                    >
                        <FormattedMessage id={linkLabel} />
                    </a>
                </span>
                <div className="text-sm text-gray-500 mt-1">
                    <FormattedMessage id="bk.walletto_terms.edition" values={{ date: version }} />
                </div>
            </div>
        </div>
    );

    return (
        <div className="fixed inset-0 z-40 overflow-y-auto">
            {/* no onClick on the overlay: this dialog cannot be dismissed */}
            <div className="fixed inset-0 bg-sky-500 opacity-30" />

            <div className="min-h-screen px-4 flex items-center justify-center">
                <div className="relative w-full max-w-2xl p-8 my-8 bg-white shadow-xl rounded-lg text-left">
                    <div className="text-xs tracking-widest uppercase text-gray-400 mb-1">
                        <FormattedMessage id="bk.walletto_terms.effective_from" values={{ date: version }} />
                    </div>

                    <h3 className="text-2xl font-bold text-gray-900 mb-4">
                        <FormattedMessage id="bk.walletto_terms.title" />
                    </h3>

                    <p className="text-gray-700 mb-6">
                        <FormattedMessage id="bk.walletto_terms.description" />
                    </p>

                    {renderTermsCheckbox(cardTermsRead, setCardTermsRead,
                        links.cardTerms, 'bk.walletto_terms.card_terms')}
                    {renderTermsCheckbox(generalTermsRead, setGeneralTermsRead,
                        links.generalTerms, 'bk.walletto_terms.general_terms')}

                    {hasError ? (
                        <p className="text-bk-red mt-2">
                            <FormattedMessage id="bk.walletto_terms.error" />
                        </p>
                    ) : null}

                    <div className="flex items-center justify-between mt-6 space-x-6">
                        <p className="text-sm text-gray-400">
                            <FormattedMessage id="bk.walletto_terms.note" />
                        </p>
                        <Button
                            label="bk.walletto_terms.btn.confirm"
                            disabled={!cardTermsRead || !generalTermsRead || isSubmitting}
                            onClick={onConfirm}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};
